import { FaultUpdate } from '@eagle/data-function-types';
import { Chip, Skeleton, styled } from '@ethings/ui-toolkit';
import { useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { FC, useMemo } from 'react';
import { isNewEvent, useDtcFaultDescriptionQuery } from '../events-table/events-table.queries';
import { useBatchFaultDataSafe } from './batch-fault-context';

interface RecommendationConfig {
  label: string;
  color: string;
  textColor?: string;
}

interface StyledRecommendationChipProps {
  chipBackground: string;
  chipColor?: string;
}

const StyledRecommendationChip = styled(Chip)<StyledRecommendationChipProps>(({ theme, chipBackground, chipColor }) => ({
  backgroundColor: chipBackground,
  color: chipColor ?? theme.palette.common.white,
  fontWeight: 500,
  width: '112px',
  borderRadius: theme.shape.borderRadius,
  '& .MuiChip-label': {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const getRecommendationColors = (theme: Theme): Record<string, RecommendationConfig> => {
  const infoColor = theme.palette.info.main;
  const serviceSoonColor = theme.palette.warning.light;
  const serviceNowColor = theme.palette.warning.main;
  const stopNowColor = theme.palette.error.main;
  const mediumColor = theme.palette.warning.dark;
  const lowColor = theme.palette.warning.light;
  const noneColor = theme.palette.grey[600];

  return {
    'informational': {
      label: 'Informational',
      color: infoColor,
      textColor: theme.palette.getContrastText(infoColor),
    },
    'service soon': {
      label: 'Service Soon',
      color: serviceSoonColor,
      textColor: theme.palette.getContrastText(serviceSoonColor),
    },
    'service now': {
      label: 'Service Now',
      color: serviceNowColor,
      textColor: theme.palette.getContrastText(serviceNowColor),
    },
    'stop now': {
      label: 'Stop Now',
      color: stopNowColor,
      textColor: theme.palette.getContrastText(stopNowColor),
    },
    // Severity fallback values
    'high': {
      label: 'High',
      color: stopNowColor,
      textColor: theme.palette.getContrastText(stopNowColor),
    },
    'medium': {
      label: 'Medium',
      color: mediumColor,
      textColor: theme.palette.getContrastText(mediumColor),
    },
    'low': {
      label: 'Low',
      color: lowColor,
      textColor: theme.palette.getContrastText(lowColor),
    },
    'none': {
      label: 'None',
      color: noneColor,
      textColor: theme.palette.getContrastText(noneColor),
    },
    'unknown': {
      label: 'Informational',
      color: infoColor,
      textColor: theme.palette.getContrastText(infoColor),
    },
  };
};

export const RecommendationLabel: FC<{ eventData: FaultUpdate; thingId: string }> = ({ eventData, thingId }) => {
  const theme = useTheme<Theme>();
  const batchContext = useBatchFaultDataSafe();
  const brandVariantId = isNewEvent(eventData) ? eventData.dtcBrandVariantId : null;

  const shouldWaitForBatch = brandVariantId !== null && batchContext?.isLoading;
  const hasBatchData = brandVariantId !== null && batchContext && brandVariantId in batchContext.faultDescriptions;
  const batchFaultDescription = hasBatchData ? batchContext.faultDescriptions[brandVariantId] : undefined;

  const faultDescriptionQuery = useDtcFaultDescriptionQuery(
    { eventData, thingId },
    { enabled: !shouldWaitForBatch && !hasBatchData },
  );

  const faultDescription = hasBatchData ? batchFaultDescription : faultDescriptionQuery.data;
  const lampCodeCorrection = faultDescription?.lampCodeCorrection;

  const isLoading = (batchContext?.isLoading && brandVariantId !== null) || (!hasBatchData && faultDescriptionQuery.isPending);

  const severity = faultDescription && 'severity' in faultDescription && typeof faultDescription.severity === 'string' ? faultDescription.severity : eventData.severity;

  const recommendationConfig = useMemo(() => {
    const recommendationColors = getRecommendationColors(theme);

    if (isLoading) return null;

    const lampCodeKey = lampCodeCorrection?.toLowerCase();

    if (lampCodeKey && recommendationColors[lampCodeKey]) {
      return recommendationColors[lampCodeKey];
    }
    const severityKey = typeof severity === 'string' ? severity.toLowerCase() : undefined;

    if (severityKey && recommendationColors[severityKey]) {
      return recommendationColors[severityKey];
    }
    return recommendationColors.unknown;
  }, [lampCodeCorrection, severity, isLoading, theme]);

  if (isLoading) {
    return <Skeleton />;
  }

  if (!recommendationConfig) {
    return null;
  }

  return (
    <StyledRecommendationChip
      label={recommendationConfig.label}
      chipBackground={recommendationConfig.color}
      chipColor={recommendationConfig.textColor}
      data-testid="fault-recommendation-label"
    />
  );
};
